import type { BlogLocale } from './types';

function faqToMarkdown(
  heading: string,
  rows: readonly { readonly q: string; readonly a: string }[],
): string {
  const chunks = [`## ${heading}`, ''];
  for (const { q, a } of rows) {
    chunks.push(`**${q}**`, '', a, '', '');
  }
  return chunks.join('\n').trimEnd();
}

/**
 * FAQ visibile nell’articolo Meta Ads MCP — stesso testo in JSON-LD FAQPage.
 * Domande formulate per intent di ricerca (MCP + Shopify + profitto + ROAS).
 */
const FAQ_EN = [
  {
    q: 'What is Meta Ads MCP for Shopify brands?',
    a: "Meta Ads MCP is Meta's documented way for supported AI agents to connect to Meta Ads using the Model Context Protocol: structured tools for reporting, diagnostics, and (with explicit permissions) campaign changes. For a Shopify brand it speeds up Meta-native work, but it does not replace Shopify, GA4, Google Ads, margins, or inventory data when you decide whether to scale.",
  },
  {
    q: 'Is Meta Ads MCP an official Meta integration?',
    a: 'Yes. Meta publishes it under Meta Ads AI Connectors, alongside components such as the Ads MCP server and Ads CLI. That makes the integration path official and documented, but you still need least-privilege access, approvals for write tools, auditability, and internal policy—same as any production Marketing API access.',
  },
  {
    q: 'Can Meta Ads MCP calculate Shopify profit or MER by itself?',
    a: 'No. Meta Ads MCP reads and acts within Meta’s view of performance; it does not automatically include Shopify net sales after discounts and refunds, COGS, payment and shipping fees, other paid channels, or stock cover. Profit, MER, and blended ROAS need those sources joined in a separate analytics layer.',
  },
  {
    q: 'Why does Meta ROAS differ from Shopify revenue or profit?',
    a: 'Meta ROAS is built from Meta attribution rules, modeled conversions, click vs view credit, account time zones, and “purchase value” as Meta records it. Shopify revenue reflects orders, refunds, discounts, taxes, and timing in your store admin. The two can diverge even when both are “correct” for their own definition—reconciliation, not one dashboard, is what makes budget decisions trustworthy.',
  },
] as const;

const FAQ_IT = [
  {
    q: "Cos'è Meta Ads MCP per chi usa Shopify?",
    a: 'È il percorso documentato da Meta per collegare agenti AI supportati a Meta Ads tramite Model Context Protocol: tool strutturati per reporting, diagnostica e, con permessi espliciti, modifiche alle campagne. Per un brand Shopify accelera il lavoro sul canale Meta, ma non sostituisce Shopify, GA4, Google Ads, margini o inventario quando devi decidere se scalare la spesa.',
  },
  {
    q: "Meta Ads MCP è un'integrazione ufficiale Meta?",
    a: 'Sì. Meta lo inserisce nei Meta Ads AI Connectors, con componenti come Ads MCP server e Ads CLI. Percorsi ufficiali e documentati riducono workaround fragili, ma restano necessari least privilege, approvazioni sui tool di scrittura, tracciabilità e policy interne—come per qualsiasi accesso programmatico a Meta Ads.',
  },
  {
    q: 'Meta Ads MCP può calcolare da solo profitto Shopify o MER?',
    a: 'No. Meta Ads MCP lavora sulla vista Meta delle performance; non include automaticamente vendite nette Shopify dopo sconti e resi, COGS, fee di pagamento e spedizione, altri canali paid o stock cover. Profitto, MER e blended ROAS richiedono che queste fonti siano unite in un layer di analytics separato.',
  },
  {
    q: 'Perché il ROAS Meta differisce da revenue o profitto in Shopify?',
    a: 'Il ROAS Meta si basa su regole di attribuzione Meta, conversioni modellate, credito click vs view, fuso orario dell’account e “purchase value” come registrato da Meta. La revenue Shopify riflette ordini, rimborsi, sconti, tasse e tempistiche nello store. Le due misure possono divergere anche quando entrambe sono corrette nel proprio perimetro: serve riconciliazione, non una sola schermata, per decisioni di budget affidabili.',
  },
] as const;

/** Blocco markdown da inserire tra §9 e §10 (corpo articolo EN/IT). */
export const META_ADS_MCP_FAQ_BODY_EN = faqToMarkdown('Quick answers', FAQ_EN);
export const META_ADS_MCP_FAQ_BODY_IT = faqToMarkdown('Domande rapide', FAQ_IT);

export function getMetaAdsMcpFaqPageJsonLd(locale: BlogLocale): {
  '@type': 'FAQPage';
  mainEntity: Array<{
    '@type': 'Question';
    name: string;
    acceptedAnswer: { '@type': 'Answer'; text: string };
  }>;
} {
  const rows = locale === 'it' ? FAQ_IT : FAQ_EN;
  return {
    '@type': 'FAQPage',
    mainEntity: rows.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  };
}
